import React, { useState, useEffect } from "react";

/**
 * PromptImprovement component showcasing prompt enhancement features
 * Displays information about prompt improvement with an interactive demo
 * 
 * @returns {JSX.Element} The rendered PromptImprovement component
 */
const PromptImprovement: React.FC = () => {
  try {
    // Log component rendering
    console.log("Rendering PromptImprovement component");

    // State for original and improved prompts
    const [originalPrompt, setOriginalPrompt] = useState("");
    const [improvedPrompt, setImprovedPrompt] = useState("");
    const [isImproving, setIsImproving] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [showImproved, setShowImproved] = useState(false);
    const [demoStarted, setDemoStarted] = useState(false);
    const [autoTypingActive, setAutoTypingActive] = useState(false);
    const [autoTypingIndex, setAutoTypingIndex] = useState(0);
    
    // Sample prompts for demonstration
    const sampleOriginalPrompt = "make me a website";
    const sampleImprovedPrompt = "Create a responsive e-commerce website with the following features:\n\n1. Homepage with featured products carousel\n2. Product catalog with filtering and sorting options\n3. User authentication system\n4. Shopping cart and checkout process\n5. Admin dashboard for inventory management\n\nThe website should use React for the frontend, Node.js for the backend, and MongoDB for the database. The design should follow modern UI/UX principles with a clean, minimalist aesthetic and a color scheme of blue and white.";
    
    // Improvement steps to show during animation
    const improvementSteps = [
      "Analyzing prompt structure...",
      "Identifying missing context...",
      "Adding specific requirements...",
      "Enhancing technical details...",
      "Refining for clarity and precision..."
    ];
    
    // Reference to store animation intervals for cleanup
    const [animationTimers, setAnimationTimers] = useState<NodeJS.Timeout[]>([]);
    
    // AI provider icons to rotate through
    const aiProviderIcons = [
      { name: "OpenAI", icon: "/ai-icons/openai.svg" },
      { name: "Anthropic", icon: "/ai-icons/anthropic.svg" },
      { name: "Gemini", icon: "/ai-icons/gemini-color.svg" },
      { name: "Mistral", icon: "/ai-icons/mistral-color.svg" },
      { name: "Cohere", icon: "/ai-icons/cohere-color.svg" },
      { name: "Meta", icon: "/ai-icons/meta-color.svg" }
    ];
    
    // Current icon index
    const [currentIconIndex, setCurrentIconIndex] = useState(0);
    
    // Function to clear all animation timers
    const clearAllTimers = () => {
      try {
        // Clear all stored timers
        animationTimers.forEach(timer => clearTimeout(timer));
        setAnimationTimers([]);
      } catch (error) {
        console.error("Error clearing animation timers:", error);
      }
    };
    
    // Function to handle improving the prompt
    const handleImprovePrompt = () => {
      try {
        if (demoStarted) return; // Prevent multiple starts
        
        // Clear any existing timers
        clearAllTimers();
        
        setDemoStarted(true);
        setIsImproving(true);
        setShowImproved(true);
        
        // Log action
        console.log("Starting prompt improvement demo");
        
        const newTimers: NodeJS.Timeout[] = [];
        
        // Animate through improvement steps
        let stepIndex = 0;
        const stepInterval = setInterval(() => {
          if (stepIndex < improvementSteps.length) {
            setImprovedPrompt(improvementSteps[stepIndex]);
            stepIndex++;
          } else {
            clearInterval(stepInterval);
            
            // Show final improved prompt with typing animation
            let charIndex = 0;
            const typingInterval = setInterval(() => {
              if (charIndex < sampleImprovedPrompt.length) {
                setImprovedPrompt(sampleImprovedPrompt.substring(0, charIndex + 1));
                charIndex++;
              } else {
                clearInterval(typingInterval);
                setIsImproving(false);
                setIsComplete(true);
                
                // Wait for a few seconds and then loop the animation
                const loopTimer = setTimeout(() => {
                  try {
                    console.log("Looping animation");
                    resetDemo();
                    
                    // Wait a bit before starting the next loop
                    const startNextLoopTimer = setTimeout(() => {
                      if (!demoStarted) {
                        handleImprovePrompt();
                      }
                    }, 2000);
                    
                    newTimers.push(startNextLoopTimer);
                  } catch (error) {
                    console.error("Error looping animation:", error);
                  }
                }, 5000); // Wait 5 seconds before looping
                
                newTimers.push(loopTimer);
              }
            }, 10);
            
            newTimers.push(typingInterval);
          }
        }, 800);
        
        newTimers.push(stepInterval);
        setAnimationTimers(newTimers);
      } catch (error) {
        // Log any errors
        console.error("Error during prompt improvement:", error);
        setIsImproving(false);
      }
    };
    
    // Auto-type the original prompt when component mounts
    useEffect(() => {
      try {
        // Start auto-typing animation after a short delay
        const autoTypeTimer = setTimeout(() => {
          if (!demoStarted && !autoTypingActive) {
            setAutoTypingActive(true);
          }
        }, 1000);
        
        // Set up icon rotation
        const iconRotationTimer = setInterval(() => {
          try {
            setCurrentIconIndex(prevIndex => (prevIndex + 1) % aiProviderIcons.length);
            console.log("Rotating AI provider icon");
          } catch (error) {
            console.error("Error rotating AI provider icon:", error);
          }
        }, 3000); // Rotate every 3 seconds
        
        setAnimationTimers([autoTypeTimer, iconRotationTimer]);
        
        return () => {
          // Clean up all timers when component unmounts
          clearAllTimers();
          clearInterval(iconRotationTimer);
        };
      } catch (error) {
        console.error("Error setting up auto-typing:", error);
      }
    }, []);
    
    // Handle auto-typing animation
    useEffect(() => {
      try {
        if (autoTypingActive && autoTypingIndex < sampleOriginalPrompt.length) {
          const typingTimer = setTimeout(() => {
            setOriginalPrompt(sampleOriginalPrompt.substring(0, autoTypingIndex + 1));
            setAutoTypingIndex(prev => prev + 1);
          }, 50);
          
          // Add timer to the list for cleanup
          setAnimationTimers(prev => [...prev, typingTimer]);
          
          return () => clearTimeout(typingTimer);
        } else if (autoTypingActive && autoTypingIndex >= sampleOriginalPrompt.length) {
          setAutoTypingActive(false);
          
          // Auto-start improvement after typing is complete
          const startTimer = setTimeout(() => {
            if (!demoStarted) {
              handleImprovePrompt();
            }
          }, 500);
          
          // Add timer to the list for cleanup
          setAnimationTimers(prev => [...prev, startTimer]);
        }
      } catch (error) {
        console.error("Error during auto-typing:", error);
        setAutoTypingActive(false);
      }
    }, [autoTypingActive, autoTypingIndex, demoStarted]);
    
    // Reset demo function
    const resetDemo = () => {
      try {
        // Log reset action
        console.log("Resetting prompt improvement demo");
        
        // Clear all animation timers
        clearAllTimers();
        
        setOriginalPrompt("");
        setImprovedPrompt("");
        setIsImproving(false);
        setIsComplete(false);
        setShowImproved(false);
        setDemoStarted(false);
        setAutoTypingActive(false);
        setAutoTypingIndex(0);
        
        // Restart auto-typing after a short delay
        const autoTypeTimer = setTimeout(() => {
          if (!autoTypingActive) {
            setAutoTypingActive(true);
          }
        }, 500);
        
        setAnimationTimers([autoTypeTimer]);
      } catch (error) {
        console.error("Error resetting demo:", error);
      }
    };

    return (
      <div id="prompt-improvement" className="inset-0">
        {/* Subtle dot pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(#6366f1 0.75px, transparent 0.75px)",
            backgroundSize: "12px 12px",
          }}
        ></div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10 py-8 md:py-12">
          <div className="overflow-hidden">
            <div className="grid grid-cols-1 items-center gap-x-6 gap-y-8 lg:grid-cols-2 p-6 sm:p-8 lg:p-10">
              
              
              {/* Prompt improvement demo */}
              <div className="relative mx-auto w-full max-w-xl lg:mx-0 lg:max-w-none lg:pl-8">
                <div className="rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Demo header */}
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <div className="flex items-center ml-2">
                        <img src="/logos/raidu_logo.png" alt="Raidu" className="h-5 w-5 mr-2" />
                        <span className="text-gray-800 text-sm font-medium">Raidu Prompt Enhancer</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div 
                        onClick={resetDemo}
                        className="relative flex cursor-pointer transition-transform hover:scale-110"
                        title={`${aiProviderIcons[currentIconIndex].name} (Reset Demo)`}
                      >
                        {/* Rotating AI provider icon */}
                        <img 
                          src={aiProviderIcons[currentIconIndex].icon} 
                          alt={aiProviderIcons[currentIconIndex].name} 
                          className="h-7 w-7 z-10 transition-opacity duration-300" 
                          key={aiProviderIcons[currentIconIndex].name} // Key helps with animation
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Content area */}
                  <div className="h-80 overflow-y-auto p-4 bg-white">
                    <div className="h-full">
                      {!showImproved ? (
                        <>
                          <div className="mb-2 text-xs text-gray-500">Your prompt:</div>
                          <div className="relative h-[calc(100%-2rem)] rounded-lg border border-gray-300 bg-white p-4">
                            <textarea
                              className="w-full h-full resize-none focus:outline-none text-sm"
                              placeholder="Enter your prompt here..."
                              value={originalPrompt}
                              onChange={(e) => {
                                if (!demoStarted && !autoTypingActive) {
                                  setOriginalPrompt(e.target.value);
                                }
                              }}
                              disabled={demoStarted}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-2 text-xs text-gray-500">Improved prompt:</div>
                          <div className="relative h-[calc(100%-2rem)] rounded-lg border border-gray-300 bg-white p-4">
                            {isImproving ? (
                              <div className="flex flex-col items-center justify-center h-full">
                                <div className="flex items-center space-x-2 mb-3">
                                  <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"></div>
                                  <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                  <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                                </div>
                                <p className="text-sm text-gray-600">{improvedPrompt}</p>
                              </div>
                            ) : (
                              <div className="h-full overflow-y-auto">
                                <pre className="text-sm whitespace-pre-wrap">{improvedPrompt}</pre>
                                {isComplete && (
                                  <div className="mt-4 text-xs text-green-600 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                    </svg>
                                    Prompt improved successfully
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Action button */}
                  <div className="border-t border-gray-200 p-3 bg-white">
                    <button
                      className={`w-full rounded-full py-2 px-4 text-sm font-medium ${
                        demoStarted
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      onClick={handleImprovePrompt}
                      disabled={demoStarted}
                    >
                      {isImproving ? "Improving Prompt..." : isComplete ? "Prompt Improved" : "Improve Prompt"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mx-auto w-full max-w-xl lg:mx-0 text-right">
                <div className="inline-block mb-4">
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent text-sm font-medium px-3 py-1 rounded-full bg-opacity-10 border border-indigo-200 bg-indigo-50">
                    Prompt Engineering
                  </span>
                </div>
                <h2 className="text-4xl font-semibold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent sm:text-5xl">
                  Intelligent Prompt Improvement
                </h2>
                <p className="mt-6 text-lg text-gray-600">
                  Raidu automatically enhances your prompts to get better AI responses. Our system analyzes your input, adds missing context, and structures the prompt for optimal results, saving time and improving output quality.
                </p>
                <div className="mt-8 flex items-center gap-x-6 justify-end">
                  <a
                    href="/booking"
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => {
                      try {
                        // Log button click
                        console.log("PromptImprovement - Get Started button clicked");
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Get Started button click:",
                          error
                        );
                      }
                    }}
                  >
                    Get Started
                  </a>
                  <a
                    href="/booking"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                    onClick={() => {
                      try {
                        // Log button click
                        console.log("PromptImprovement - Learn More button clicked");
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Learn More button click:",
                          error
                        );
                      }
                    }}
                  >
                    Learn More <span aria-hidden="true">&rarr;</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error("Error rendering PromptImprovement component:", error);

    // Return error state
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base font-semibold text-indigo-600">Error</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Failed to load prompt improvement section
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }
};

export default PromptImprovement;
